import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import "@/hrNotifications.css";
import { Bell as PhosphorBell } from "@phosphor-icons/react";
import {
  Bell,
  FileText,
  CreditCard,
  MessageSquare,
  AlertTriangle,
  X,
  UserPlus,
  UserMinus,
  BarChart3,
  FileAudio,
  ShieldOff,
  Trash2,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DeadlineAlertsPanel,
  useDeadlineAlerts,
} from "@/components/DeadlineAlertBell";

const DEADLINE_TYPES = new Set(["deadline_warning", "deadline_overdue"]);

const TYPE_CONFIG: Record<string, { icon: typeof Bell; bg: string }> = {
  new_contract: { icon: FileText, bg: "bg-slate-700" },
  invoice_sent: { icon: CreditCard, bg: "bg-slate-500" },
  invoice_paid: { icon: CreditCard, bg: "bg-emerald-600" },
  invoice_overdue: { icon: AlertTriangle, bg: "bg-red-500" },
  invoice_comment: { icon: MessageSquare, bg: "bg-slate-600" },
  chat_mention: { icon: MessageSquare, bg: "bg-slate-800" },
  recruiter_assigned: { icon: UserPlus, bg: "bg-blue-600" },
  recruiter_unassigned: { icon: UserMinus, bg: "bg-orange-500" },
  closing_probability: { icon: BarChart3, bg: "bg-violet-600" },
  // Видеоинтервью. Красный — там, где надо что-то сделать прямо сейчас: отзыв
  // согласия гасит ссылку у заказчика, и ему надо позвонить.
  video_transcript_ready: { icon: FileAudio, bg: "bg-emerald-600" },
  video_transcript_failed: { icon: AlertTriangle, bg: "bg-orange-500" },
  video_consent_withdrawn: { icon: ShieldOff, bg: "bg-red-600" },
  video_deletion_requested: { icon: Trash2, bg: "bg-amber-600" },
  video_deletion_decided: { icon: Trash2, bg: "bg-slate-600" },
};

function timeAgo(date: Date | string): string {
  const now = new Date();
  const d = new Date(date);
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "сейчас";
  if (diffMin < 60) return `${diffMin} мин`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} ч`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays === 1) return "вчера";
  return `${diffDays} дн`;
}

const INITIAL_READ_COUNT = 5;
const LOAD_MORE_COUNT = 7;

export function NotificationBell({
  includeDeadlines = false,
}: {
  includeDeadlines?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("events");
  const [, navigate] = useLocation();
  const [readLimit, setReadLimit] = useState(INITIAL_READ_COUNT);
  const deadlineQuery = useDeadlineAlerts(includeDeadlines);

  const { data: unreadCount = 0, refetch: refetchCount } =
    trpc.notifications.getUnreadCount.useQuery(undefined, {
      // Один источник запроса — на mount + интервал. staleTime=20s + refetchOnMount=false
      // убирают дублирующий запрос от Strict Mode / повторного монтирования.
      refetchInterval: 30_000,
      staleTime: 20_000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  const {
    data: rawNotifications = [],
    refetch,
    isLoading: notificationsLoading,
    isError: notificationsError,
  } = trpc.notifications.getAll.useQuery(undefined, {
    enabled: open,
  });

  const notifications = rawNotifications.filter(
    (n: any) => !DEADLINE_TYPES.has(n.type)
  );
  const deadlineCount = deadlineQuery.totalAlerts;

  const { unread, read, hasMoreRead } = useMemo(() => {
    const unread = notifications.filter((n: any) => !n.isRead);
    const allRead = notifications.filter((n: any) => n.isRead);
    const read = allRead.slice(0, readLimit);
    return { unread, read, hasMoreRead: allRead.length > readLimit };
  }, [notifications, readLimit]);

  const markAsRead = trpc.notifications.markAsRead.useMutation({
    onSuccess: () => {
      refetch();
      refetchCount();
    },
  });

  const markAllAsRead = trpc.notifications.markAllAsRead.useMutation({
    onSuccess: () => {
      refetch();
      refetchCount();
    },
  });

  const handleClick = (notif: (typeof notifications)[0]) => {
    if (!notif.isRead) {
      markAsRead.mutate({ id: notif.id });
    }
    const vacancyId = (() => {
      if (notif.link) {
        const url = new URL(notif.link, window.location.origin);
        const v = url.searchParams.get("vacancy");
        if (v) return Number(v);
      }
      return (notif as any).relatedVacancyId ?? null;
    })();

    if (notif.link) {
      const url = new URL(notif.link, window.location.origin);
      // Add vacancy param if missing but we have relatedVacancyId
      if (
        !url.searchParams.get("vacancy") &&
        vacancyId &&
        url.pathname === "/kanban"
      ) {
        url.searchParams.set("vacancy", String(vacancyId));
      }
      navigate(url.pathname + url.search);
    } else if (vacancyId) {
      navigate(`/kanban?vacancy=${vacancyId}`);
    }

    if (vacancyId) {
      window.dispatchEvent(
        new CustomEvent("open-vacancy", {
          detail: {
            vacancyId,
            tab: notif.type === "chat_mention" ? "comments" : undefined,
          },
        })
      );
    }
    closePopover();
  };

  const renderNotif = (notif: (typeof notifications)[0]) => {
    const config = TYPE_CONFIG[notif.type] ?? {
      icon: Bell,
      bg: "bg-slate-400",
    };
    const Icon = config.icon;
    return (
      <button
        key={notif.id}
        onClick={() => handleClick(notif)}
        className={
          includeDeadlines
            ? `hr-notification-item${notif.isRead ? " is-read" : " is-unread"}`
            : `w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
                !notif.isRead
                  ? "bg-white hover:bg-slate-50"
                  : "hover:bg-slate-50/60"
              }`
        }
      >
        <div
          data-kind={notif.type}
          className={`mt-0.5 shrink-0 w-8 h-8 rounded-[10px] ${includeDeadlines ? "hr-notification-kind hr-notification-icon" : `${config.bg} flex items-center justify-center shadow-sm`}`}
        >
          <Icon
            className={`h-4 w-4 ${includeDeadlines ? "text-current" : "text-white"}`}
            strokeWidth={2.2}
          />
        </div>
        <div
          className={
            includeDeadlines ? "hr-notification-copy" : "flex-1 min-w-0"
          }
        >
          <div
            className={
              includeDeadlines
                ? "hr-notification-meta"
                : "flex items-baseline justify-between gap-2"
            }
          >
            <p
              className={
                includeDeadlines
                  ? `hr-notification-title${notif.isRead ? " is-read" : " is-unread"}`
                  : `text-[13px] leading-snug ${!notif.isRead ? "font-semibold text-ink-title" : "font-medium text-ink-secondary"}`
              }
            >
              {notif.title}
            </p>
            <span
              className={
                includeDeadlines
                  ? "hr-notification-time"
                  : `text-[11px] shrink-0 tabular-nums ${!notif.isRead ? "text-ink" : "text-ink-secondary"}`
              }
            >
              {timeAgo(notif.createdAt)}
            </span>
          </div>
          <p
            className={
              includeDeadlines
                ? "hr-notification-message"
                : "text-[13px] leading-relaxed mt-0.5 whitespace-pre-line text-ink-secondary"
            }
          >
            {notif.message}
          </p>
        </div>
        {!notif.isRead && (
          <div
            className={
              includeDeadlines
                ? "hr-notification-unread-dot"
                : "mt-3 shrink-0 w-[9px] h-[9px] rounded-full bg-slate-800"
            }
          />
        )}
      </button>
    );
  };

  const notificationContent =
    includeDeadlines && notificationsLoading ? (
      <div className="hr-notification-state">Загружаем события…</div>
    ) : includeDeadlines && notificationsError ? (
      <div className="hr-notification-state" role="alert">
        Не удалось загрузить события
      </div>
    ) : unread.length === 0 && read.length === 0 ? (
      <div
        className={
          includeDeadlines ? "hr-notification-empty" : "px-4 py-12 text-center"
        }
      >
        <div
          className={
            includeDeadlines
              ? "hr-notification-empty-icon"
              : "w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3"
          }
        >
          <Bell
            className={includeDeadlines ? "w-5 h-5" : "w-5 h-5 text-slate-300"}
          />
        </div>
        <p
          className={
            includeDeadlines
              ? "hr-notification-state"
              : "text-[13px] text-ink-secondary"
          }
        >
          Нет уведомлений
        </p>
      </div>
    ) : (
      <div>
        {unread.length > 0 && (
          <div
            className={
              includeDeadlines
                ? "hr-notification-group"
                : "divide-y divide-slate-100/60"
            }
          >
            {unread.map(renderNotif)}
          </div>
        )}
        {read.length > 0 && (
          <div>
            {unread.length > 0 && (
              <div
                className={
                  includeDeadlines
                    ? "hr-notification-earlier"
                    : "px-4 py-1.5 border-t border-slate-100/80"
                }
              >
                <span
                  className={
                    includeDeadlines
                      ? "hr-notification-earlier-label"
                      : "text-[11px] font-medium text-ink-secondary uppercase tracking-wider"
                  }
                >
                  Ранее
                </span>
              </div>
            )}
            <div
              className={
                includeDeadlines
                  ? "hr-notification-group is-read-group"
                  : "divide-y divide-slate-50"
              }
            >
              {read.map(renderNotif)}
            </div>
            {hasMoreRead && (
              <button
                onClick={() => setReadLimit(prev => prev + LOAD_MORE_COUNT)}
                className={
                  includeDeadlines
                    ? "hr-notification-action hr-notification-more"
                    : "w-full flex items-center justify-center gap-1 px-4 py-2.5 text-[13px] font-medium text-ink-link hover:bg-slate-50 transition-colors border-t border-slate-100/60"
                }
              >
                Показать ещё
              </button>
            )}
          </div>
        )}
      </div>
    );

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setReadLimit(INITIAL_READ_COUNT);
      setActiveTab("events");
    }
  };

  const closePopover = () => {
    setOpen(false);
    setReadLimit(INITIAL_READ_COUNT);
    setActiveTab("events");
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            includeDeadlines
              ? `Уведомления: непрочитанных ${unreadCount}; дедлайнов ${deadlineCount}`
              : `Уведомления: непрочитанных ${unreadCount}`
          }
          title={
            includeDeadlines
              ? `Непрочитанных событий: ${unreadCount}. Дедлайнов: ${deadlineCount}.`
              : `Непрочитанных уведомлений: ${unreadCount}`
          }
          className={
            includeDeadlines
              ? "hr-notification-trigger"
              : "relative size-8 rounded-lg hover:bg-neutral-100"
          }
        >
          {includeDeadlines ? (
            <PhosphorBell className="size-5" weight="regular" />
          ) : (
            <Bell className="size-4 text-neutral-500" />
          )}
          {includeDeadlines && unreadCount > 0 && (
            <span className="min-w-4 text-xs leading-4 font-semibold">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
          {includeDeadlines && unreadCount === 0 && deadlineCount > 0 && (
            <span aria-hidden="true" className="hr-notification-deadline-dot" />
          )}
          {!includeDeadlines && unreadCount > 0 && (
            <span className="absolute right-1 top-1 flex items-center justify-center rounded-full bg-[oklch(0.556_0_0)] text-[9px] font-semibold text-white min-w-[14px] h-3.5 px-0.5 border border-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={
          includeDeadlines
            ? "hr-notification-popover w-[min(420px,calc(100vw-24px))] overflow-hidden rounded-2xl p-0"
            : "w-[min(400px,calc(100vw-24px))] p-0 rounded-2xl shadow-[0_8px_30px_-4px_rgba(0,0,0,0.15)] border-0 overflow-hidden bg-white/95 backdrop-blur-xl"
        }
        align="end"
        collisionPadding={includeDeadlines ? 12 : undefined}
      >
        <div
          className={
            includeDeadlines
              ? "hr-notification-header"
              : "flex items-center justify-between px-4 py-3 border-b border-slate-100/80"
          }
        >
          <h3
            className={
              includeDeadlines
                ? "hr-notification-heading"
                : "font-semibold text-[15px] text-ink-title tracking-tight"
            }
          >
            Уведомления
          </h3>
          <div
            className={
              includeDeadlines
                ? "hr-notification-header-actions"
                : "flex items-center gap-2"
            }
          >
            {unreadCount > 0 &&
              (!includeDeadlines || activeTab === "events") && (
                <button
                  onClick={() => markAllAsRead.mutate()}
                  className={
                    includeDeadlines
                      ? "hr-notification-action hr-notification-mark-all"
                      : "text-[13px] font-medium text-ink-link hover:opacity-80 transition-opacity"
                  }
                >
                  Прочитать все
                </button>
              )}
            <button
              onClick={closePopover}
              aria-label="Закрыть уведомления"
              className={
                includeDeadlines
                  ? "hr-notification-action hr-notification-close"
                  : "h-6 w-6 flex items-center justify-center rounded-md hover:bg-slate-100 transition-colors"
              }
            >
              <X className="h-4 w-4 text-ink-secondary" />
            </button>
          </div>
        </div>
        {includeDeadlines && (
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="hr-notification-tabs gap-0"
          >
            <TabsList className="hr-notification-tabs-list grid w-[calc(100%-24px)] grid-cols-2">
              <TabsTrigger value="events" className="hr-notification-tab">
                События
                {unreadCount > 0
                  ? ` · ${unreadCount > 99 ? "99+" : unreadCount}`
                  : ""}
              </TabsTrigger>
              <TabsTrigger value="deadlines" className="hr-notification-tab">
                Дедлайны
                {deadlineCount > 0
                  ? ` · ${deadlineCount > 99 ? "99+" : deadlineCount}`
                  : ""}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="deadlines" className="mt-0 min-h-0">
              {deadlineQuery.isLoading ? (
                <div className="hr-notification-state" role="status">
                  Загружаем дедлайны…
                </div>
              ) : deadlineQuery.isError ? (
                <div className="hr-notification-state" role="alert">
                  Не удалось загрузить дедлайны
                </div>
              ) : (
                <DeadlineAlertsPanel
                  alerts={deadlineQuery.alerts}
                  variant="hr"
                  className="hr-notification-deadline-scroll"
                  onSelect={vacancyId => {
                    navigate(`/kanban?vacancy=${vacancyId}`);
                    window.dispatchEvent(
                      new CustomEvent("open-vacancy", { detail: { vacancyId } })
                    );
                    closePopover();
                  }}
                />
              )}
            </TabsContent>
            <TabsContent value="events" className="mt-0">
              <div className="hr-notification-events-panel max-h-[min(420px,calc(100dvh-150px))] overflow-y-auto">
                {notificationContent}
              </div>
            </TabsContent>
          </Tabs>
        )}
        {!includeDeadlines && (
          <div className="max-h-[420px] overflow-y-auto">
            {notificationContent}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
